/**
 * Tuition To Advance
 * Cart expiry system
 *
 * Every Shopify cart item expires 15 minutes after it is added to the cart.
 */

(() => {
  "use strict";

  const EXPIRY_MINUTES = 15;
  const EXPIRY_MILLISECONDS = EXPIRY_MINUTES * 60 * 1000;
  const EXPIRY_PROPERTY = "_tta_cart_expires_at";

  const SHOPIFY_ROOT =
    window.Shopify?.routes?.root || "/";

  const CART_URL =
    `${SHOPIFY_ROOT}cart.js`;

  const CART_CHANGE_URL =
    `${SHOPIFY_ROOT}cart/change.js`;

  let syncInProgress = false;
  let lastCart = null;
  let countdownInterval = null;
  let automaticCheckInterval = null;
  let checkoutInProgress = false;
  let expiredMessageVisible = false;

  /*
   * Use the original fetch function for internal requests.
   * This prevents the cart-change observer from triggering itself.
   */
  const originalFetch =
    window.fetch.bind(window);

  /*
   * All valid Shopify cart lines now use the expiry system.
   *
   * Previously, this only returned true for booking products containing
   * calendar, tutor, date and time properties. That prevented the timer from
   * appearing for ordinary products.
   */
  function isExpiringItem(item) {
    return Boolean(
      item &&
      item.key &&
      Number(item.quantity) > 0
    );
  }

  function getExpiryTimestamp(item) {
    const expiryValue =
      item?.properties?.[EXPIRY_PROPERTY];

    if (!expiryValue) {
      return null;
    }

    const expiryTimestamp =
      Date.parse(expiryValue);

    return Number.isFinite(expiryTimestamp)
      ? expiryTimestamp
      : null;
  }

  async function fetchCart() {
    const response = await originalFetch(
      CART_URL,
      {
        method: "GET",
        headers: {
          Accept: "application/json"
        },
        credentials: "same-origin",
        cache: "no-store"
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to load cart: ${response.status}`
      );
    }

    return response.json();
  }

  async function changeCartLine(payload) {
    const response = await originalFetch(
      CART_CHANGE_URL,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorBody =
        await response.text();

      throw new Error(
        `Unable to update cart: ${response.status} ${errorBody}`
      );
    }

    return response.json();
  }

  /*
   * Add an expiry timestamp to every cart item that does not yet have one.
   *
   * Existing line-item properties are preserved because Shopify replaces the
   * complete properties object when line-item properties are changed.
   */
  async function stampMissingExpiryTimes(cart) {
    let currentCart = cart;
    let cartChanged = false;

    while (true) {
      const missingExpiryIndex =
        currentCart.items.findIndex(item => {
          return (
            isExpiringItem(item) &&
            !getExpiryTimestamp(item)
          );
        });

      if (missingExpiryIndex === -1) {
        break;
      }

      const item =
        currentCart.items[missingExpiryIndex];

      const expiryTime =
        new Date(
          Date.now() + EXPIRY_MILLISECONDS
        ).toISOString();

      const existingProperties = {
        ...(item.properties || {})
      };

      existingProperties[EXPIRY_PROPERTY] =
        expiryTime;

      currentCart = await changeCartLine({
        line: missingExpiryIndex + 1,
        quantity: item.quantity,
        properties: existingProperties
      });

      cartChanged = true;
    }

    return {
      cart: currentCart,
      changed: cartChanged
    };
  }

  async function removeExpiredItems(cart) {
    let currentCart = cart;
    const removedItems = [];

    /*
     * Work from the final line backwards so cart line positions remain safe.
     */
    const expiredItems =
      currentCart.items
        .map((item, index) => ({
          item,
          line: index + 1
        }))
        .filter(({ item }) => {
          if (!isExpiringItem(item)) {
            return false;
          }

          const expiryTimestamp =
            getExpiryTimestamp(item);

          return (
            expiryTimestamp !== null &&
            Date.now() >= expiryTimestamp
          );
        })
        .reverse();

    for (const expired of expiredItems) {
      removedItems.push(expired.item);

      currentCart = await changeCartLine({
        id: expired.item.key,
        quantity: 0
      });
    }

    return {
      cart: currentCart,
      removedItems
    };
  }

  function getExpiringItems(cart) {
    return (cart?.items || []).filter(
      isExpiringItem
    );
  }

  function getEarliestExpiry(cart) {
    const expiryTimes =
      getExpiringItems(cart)
        .map(getExpiryTimestamp)
        .filter(timestamp => timestamp !== null);

    if (!expiryTimes.length) {
      return null;
    }

    return Math.min(...expiryTimes);
  }

  function formatRemainingTime(milliseconds) {
    const safeMilliseconds =
      Math.max(0, milliseconds);

    const totalSeconds =
      Math.ceil(safeMilliseconds / 1000);

    const minutes =
      Math.floor(totalSeconds / 60);

    const seconds =
      totalSeconds % 60;

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }

  function isCartPage() {
    const pathname =
      window.location.pathname.replace(/\/+$/, "");

    const cartPath =
      `${SHOPIFY_ROOT}cart`.replace(/\/+$/, "");

    return (
      pathname === cartPath ||
      pathname.endsWith("/cart")
    );
  }

  function ensureStyles() {
    if (
      document.getElementById(
        "tta-booking-expiry-styles"
      )
    ) {
      return;
    }

    const style =
      document.createElement("style");

    style.id =
      "tta-booking-expiry-styles";

    style.textContent = `
      #tta-booking-expiry-notice {
        box-sizing: border-box;
        max-width: 1180px;
        margin: 18px auto;
        padding: 16px 20px;
        border: 1px solid #b9dff2;
        border-radius: 12px;
        background: #eef8fd;
        color: #0b2f4b;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 15px;
        line-height: 1.6;
      }

      #tta-booking-expiry-notice strong {
        color: #0073c8;
      }

      #tta-booking-expiry-notice.tta-expired {
        border-color: #f0c5c5;
        background: #fff2f2;
        color: #8a1f1f;
      }

      #tta-booking-expiry-floating {
        position: fixed;
        right: 18px;
        bottom: 18px;
        z-index: 9999;
        max-width: 350px;
        padding: 14px 18px;
        border: 1px solid #b9dff2;
        border-radius: 12px;
        background: #ffffff;
        box-shadow: 0 8px 28px rgba(11, 47, 75, 0.16);
        color: #0b2f4b;
        font-family: Arial, Helvetica, sans-serif;
        font-size: 14px;
        line-height: 1.5;
      }

      #tta-booking-expiry-floating strong {
        color: #0073c8;
      }

      @media (max-width: 600px) {
        #tta-booking-expiry-floating {
          right: 12px;
          bottom: 12px;
          left: 12px;
          max-width: none;
        }

        #tta-booking-expiry-notice {
          margin: 12px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function findCartContentContainer() {
    return (
      document.querySelector(
        ".cart"
      ) ||
      document.querySelector(
        "main"
      ) ||
      document.querySelector(
        "#MainContent"
      ) ||
      document.body
    );
  }

  function showCartPageNotice() {
    if (!isCartPage()) {
      return null;
    }

    ensureStyles();

    let notice =
      document.getElementById(
        "tta-booking-expiry-notice"
      );

    if (!notice) {
      notice =
        document.createElement("div");

      notice.id =
        "tta-booking-expiry-notice";

      const container =
        findCartContentContainer();

      container.insertBefore(
        notice,
        container.firstChild
      );
    }

    return notice;
  }

  function showFloatingTimer() {
    ensureStyles();

    let notice =
      document.getElementById(
        "tta-booking-expiry-floating"
      );

    if (!notice) {
      notice =
        document.createElement("div");

      notice.id =
        "tta-booking-expiry-floating";

      document.body.appendChild(notice);
    }

    return notice;
  }

  function removeNotices() {
    const cartNotice =
      document.getElementById(
        "tta-booking-expiry-notice"
      );

    /*
     * Keep the expired message visible on the cart page until another item is
     * added or the customer leaves the page.
     */
    if (
      cartNotice &&
      !(
        expiredMessageVisible &&
        cartNotice.classList.contains(
          "tta-expired"
        )
      )
    ) {
      cartNotice.remove();
    }

    document
      .getElementById(
        "tta-booking-expiry-floating"
      )
      ?.remove();
  }

  function updateCountdownDisplay() {
    if (!lastCart) {
      return;
    }

    const expiringItems =
      getExpiringItems(lastCart);

    if (!expiringItems.length) {
      removeNotices();
      return;
    }

    const earliestExpiry =
      getEarliestExpiry(lastCart);

    if (!earliestExpiry) {
      return;
    }

    expiredMessageVisible = false;

    const remaining =
      earliestExpiry - Date.now();

    const formattedTime =
      formatRemainingTime(remaining);

    const message = `
      Items in your basket expire after ${EXPIRY_MINUTES} minutes.
      Time remaining:
      <strong>${formattedTime}</strong>.
      Complete checkout before the timer expires.
    `;

    const cartNotice =
      showCartPageNotice();

    if (cartNotice) {
      cartNotice.classList.remove(
        "tta-expired"
      );

      cartNotice.innerHTML = message;
    }

    /*
     * Show a compact timer outside the cart page as well.
     */
    if (!isCartPage()) {
      const floatingNotice =
        showFloatingTimer();

      floatingNotice.innerHTML = message;
    } else {
      document
        .getElementById(
          "tta-booking-expiry-floating"
        )
        ?.remove();
    }

    if (remaining <= 0) {
      syncCartExpiry({
        reloadCartPage: true
      });
    }
  }

  function showExpiredMessage() {
    if (!isCartPage()) {
      return;
    }

    const notice =
      showCartPageNotice();

    if (!notice) {
      return;
    }

    expiredMessageVisible = true;

    notice.classList.add(
      "tta-expired"
    );

    notice.innerHTML = `
      <strong>Your basket item expired.</strong>
      One or more items were removed because checkout was not completed
      within ${EXPIRY_MINUTES} minutes. Please add the item again to continue.
    `;
  }

  function hasExpiredQueryParameter() {
    const parameters =
      new URLSearchParams(
        window.location.search
      );

    return (
      parameters.get(
        "booking_expired"
      ) === "1"
    );
  }

  function notifyThemeOfCartChange(cart) {
    /*
     * These events cover several common Shopify theme implementations.
     * The page is reloaded on the full cart page to guarantee an accurate UI.
     */
    document.dispatchEvent(
      new CustomEvent(
        "cart:updated",
        {
          bubbles: true,
          detail: {
            cart
          }
        }
      )
    );

    document.documentElement.dispatchEvent(
      new CustomEvent(
        "cart:refresh",
        {
          bubbles: true,
          detail: {
            cart
          }
        }
      )
    );

    updateVisibleCartCounts(
      cart.item_count
    );
  }

  function updateVisibleCartCounts(itemCount) {
    const selectors = [
      "[data-cart-count]",
      ".cart-count",
      "#CartCount",
      ".cart-count-bubble span[aria-hidden='true']",
      ".header__icon--cart .cart-count-bubble span"
    ];

    selectors.forEach(selector => {
      document
        .querySelectorAll(selector)
        .forEach(element => {
          element.textContent =
            String(itemCount);
        });
    });

    if (itemCount === 0) {
      document
        .querySelectorAll(
          ".cart-count-bubble"
        )
        .forEach(element => {
          element.style.display =
            "none";
        });
    }
  }

  function buildCartExpiryUrl() {
    const base =
      `${SHOPIFY_ROOT}cart`;

    return `${base}?booking_expired=1`;
  }

  async function syncCartExpiry({
    reloadCartPage = false
  } = {}) {
    if (syncInProgress) {
      return {
        cart: lastCart,
        removedItems: []
      };
    }

    syncInProgress = true;

    try {
      let cart =
        await fetchCart();

      const stampedResult =
        await stampMissingExpiryTimes(
          cart
        );

      cart =
        stampedResult.cart;

      const removalResult =
        await removeExpiredItems(
          cart
        );

      cart =
        removalResult.cart;

      lastCart = cart;

      notifyThemeOfCartChange(cart);
      updateCountdownDisplay();

      if (
        removalResult.removedItems.length > 0
      ) {
        sessionStorage.setItem(
          "tta_cart_expired",
          "1"
        );

        if (
          isCartPage() &&
          reloadCartPage
        ) {
          window.location.replace(
            buildCartExpiryUrl()
          );

          return {
            cart,
            removedItems:
              removalResult.removedItems
          };
        }

        if (!isCartPage()) {
          removeNotices();
        }
      }

      return {
        cart,
        removedItems:
          removalResult.removedItems
      };
    } catch (error) {
      console.error(
        "TTA cart expiry error:",
        error
      );

      return {
        cart: lastCart,
        removedItems: [],
        error
      };
    } finally {
      syncInProgress = false;
    }
  }

  function scheduleCartSync() {
    window.setTimeout(() => {
      syncCartExpiry({
        reloadCartPage: true
      });
    }, 250);
  }

  /*
   * Watch Shopify Ajax cart requests.
   *
   * This makes the timer start immediately after any product is added,
   * including when the theme uses a cart drawer.
   */
  window.fetch = async (...argumentsList) => {
    const response =
      await originalFetch(
        ...argumentsList
      );

    try {
      const requestInput =
        argumentsList[0];

      const requestUrl =
        typeof requestInput === "string"
          ? requestInput
          : requestInput?.url || "";

      const isCartMutation =
        /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/i.test(
          requestUrl
        );

      if (isCartMutation) {
        scheduleCartSync();
      }
    } catch (error) {
      console.warn(
        "Unable to observe Shopify cart update:",
        error
      );
    }

    return response;
  };

  async function handleCheckoutAttempt(
    destination
  ) {
    if (checkoutInProgress) {
      return;
    }

    checkoutInProgress = true;

    try {
      const result =
        await syncCartExpiry();

      if (
        result.removedItems?.length
      ) {
        window.location.assign(
          buildCartExpiryUrl()
        );

        return;
      }

      window.location.assign(
        destination ||
        `${SHOPIFY_ROOT}checkout`
      );
    } finally {
      checkoutInProgress = false;
    }
  }

  /*
   * Check checkout links before allowing the customer to continue.
   */
  document.addEventListener(
    "click",
    event => {
      const checkoutLink =
        event.target.closest(
          "a[href*='/checkout']"
        );

      if (!checkoutLink) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      handleCheckoutAttempt(
        checkoutLink.href
      );
    },
    true
  );

  /*
   * Check cart forms that use a checkout submit button.
   */
  document.addEventListener(
    "submit",
    event => {
      const form =
        event.target;

      if (
        !(form instanceof HTMLFormElement)
      ) {
        return;
      }

      const submitter =
        event.submitter;

      const isCheckoutSubmission =
        submitter?.name === "checkout" ||
        form.action.includes(
          "/checkout"
        );

      if (!isCheckoutSubmission) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      handleCheckoutAttempt(
        `${SHOPIFY_ROOT}checkout`
      );
    },
    true
  );

  function beginAutomaticChecks() {
    if (!countdownInterval) {
      countdownInterval =
        window.setInterval(
          updateCountdownDisplay,
          1000
        );
    }

    if (!automaticCheckInterval) {
      automaticCheckInterval =
        window.setInterval(() => {
          syncCartExpiry({
            reloadCartPage: true
          });
        }, 15000);
    }
  }

  document.addEventListener(
    "visibilitychange",
    () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        syncCartExpiry({
          reloadCartPage: true
        });
      }
    }
  );

  window.addEventListener(
    "focus",
    () => {
      syncCartExpiry({
        reloadCartPage: true
      });
    }
  );

  window.addEventListener(
    "pageshow",
    () => {
      syncCartExpiry({
        reloadCartPage: true
      });
    }
  );

  async function initialise() {
    const shouldShowExpiredMessage =
      hasExpiredQueryParameter() ||
      sessionStorage.getItem(
        "tta_cart_expired"
      ) === "1" ||
      sessionStorage.getItem(
        "tta_booking_expired"
      ) === "1";

    sessionStorage.removeItem(
      "tta_cart_expired"
    );

    sessionStorage.removeItem(
      "tta_booking_expired"
    );

    await syncCartExpiry({
      reloadCartPage: true
    });

    if (shouldShowExpiredMessage) {
      showExpiredMessage();
    }

    beginAutomaticChecks();
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialise
    );
  } else {
    initialise();
  }
})();
